import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { ProyectosService } from "../../proyectos.service";
import { ModalAgregarRiesgo } from "../modal-agregar-riesgo/modal-agregar-riesgo.component";
import { Riesgos } from "src/app/shared/model/gestion-proyectos/riesgos/riesgo.model";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { HttpParams } from "@angular/common/http";
import { ModalSeguimientoRiesgoComponent } from "../../admin-proyectos/modal-seguimiento-riesgo/modal-seguimiento-riesgo.component";

@Component({
  selector: "app-consulta-riesgo",
  standalone: true,
  templateUrl: "./consulta-riesgos.component.html",
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule],
})
export class ConsultaRiesgosComponent {
  @Input() proyectoSeleccionado!: Proyecto;
  @Output() mostrarPrincipal = new EventEmitter<boolean>();
  @Output() mostrarMascaraConsulta = new EventEmitter<boolean>();
  @ViewChild("downloadReportesLink") private downloadLink!: ElementRef<HTMLAnchorElement>;

  dialogRef: MatDialogRef<any>;
  consultaObject = new ConsultaList<Riesgos>();
  listRiesgos: Array<Riesgos> = [];
  fileUrl = "";
  fechaActual = new Date();

  totalRegistros = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  constructor(
    private modalDialog: MatDialog,
    private msg: Msg,
    private service: ProyectosService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.proyectoSeleccionado?.id) {
      return;
    }
    this.consultaPagina(1);
  }

  consultaPagina(page = 1): void {
    this.currentPage = Math.max(page, 1);
    this.loading = this.listRiesgos.length === 0;
    this.mostrarMascaraConsulta.emit(true);
    this.cdr.detectChanges();

    this.consultaObject.regInicial = (this.currentPage - 1) * this.pageSize;
    this.consultaObject.regXPagina = this.pageSize;
    this.consultaObject.bean = new Riesgos();
    this.consultaObject.bean.id_proyecto = this.proyectoSeleccionado.id.toString();
    this.consultaObject.param1 = "proyecto";
    this.consultaObject.sort = "id";
    this.consultaObject.param4 = Constantes.UNO;

    this.service
      .consultaRiesgoPaginacion(this.consultaObject)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.mostrarMascaraConsulta.emit(false);
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.listRiesgos = Array.isArray(res?.list) ? [...res.list] : [];
          this.totalRegistros = this.resolveTotalRegistros(res, this.listRiesgos.length);

          const maxPages = this.totalPages;
          if (maxPages > 0 && this.currentPage > maxPages) {
            this.consultaPagina(maxPages);
            return;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.listRiesgos = [];
          this.totalRegistros = 0;
          this.msg.warningCall("Error al consultar datos", null, null);
        },
      });
  }

  openModRiesgo(riesgo: Riesgos): void {
    this.dialogRef = this.modalDialog.open(ModalAgregarRiesgo, {
      disableClose: true,
      autoFocus: true,
      width: "60%",
      panelClass: "custom-modalbox",
      data: { idProyecto: this.proyectoSeleccionado.id, riesgo },
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  agregarSeguimiento(riesgo: Riesgos): void {
    this.dialogRef = this.modalDialog.open(ModalSeguimientoRiesgoComponent, {
      disableClose: true,
      autoFocus: true,
      maxHeight: "900px",
      width: "1200px",
      panelClass: "custom-modalbox",
      data: { riesgo },
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  descargarProyecto(): void {
    const anioActual = this.fechaActual.getFullYear();
    const httpParams = new HttpParams()
      .set("fecha", "hola")
      .set("inicio", `01/01/${anioActual}`)
      .set("fin", `31/12/${anioActual}`)
      .set("creditoId", "2")
      .set("idProyecto", this.proyectoSeleccionado.id)
      .set("idArea", "0");

    this.service.getReporteRiesgosExcel(httpParams).subscribe((response) => {
      if (response?.type !== "application/vnd.ms-excel") {
        return;
      }

      const file = new Blob([response], { type: "application/vnd.ms-excel" });
      this.fileUrl = window.URL.createObjectURL(file);
      const link = this.downloadLink.nativeElement;
      link.href = this.fileUrl;
      link.download = "FOS-005_Plan_de_Respuesta_al_Riesgo";
      link.click();
      window.URL.revokeObjectURL(this.fileUrl);
    });
  }

  regresar(): void {
    this.mostrarPrincipal.emit(true);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getDateLabel(riesgo: Riesgos): string {
    if (!riesgo?.fecha_identificacion) {
      return "Sin fecha";
    }
    return new Date(riesgo.fecha_identificacion).toLocaleDateString();
  }

  getSeverityLabel(riesgo: Riesgos): string {
    return riesgo?.severidad?.descripcion || "Sin severidad";
  }

  getSeverityClass(riesgo: Riesgos): string {
    const severityText = (riesgo?.severidad?.descripcion || "").toUpperCase();
    const severityValue = Number(riesgo?.calculo_severidad ?? 0);

    if (severityText.includes("ALTA") || severityValue >= 8) {
      return "is-danger";
    }

    if (severityText.includes("MEDIA") || severityValue >= 4) {
      return "is-pending";
    }

    return "is-active";
  }

  getRiskPhase(riesgo: Riesgos): string {
    return riesgo?.faseDeteccion?.nombre_faseDeteccion || "Sin fase";
  }

  getProbabilityLabel(riesgo: Riesgos): string {
    return riesgo?.probabilidad?.nombre || "Sin probabilidad";
  }

  getResponseStrategy(riesgo: Riesgos): string {
    return riesgo?.estrategia?.estrategia || "Sin estrategia";
  }

  getPeriodicityLabel(riesgo: Riesgos): string {
    return riesgo?.periodicidad?.descripcion || "Sin periodicidad";
  }

  getActionsSummary(riesgo: Riesgos): string {
    const text = riesgo?.acciones_seguir || "Sin acciones definidas";
    return text.length > 140 ? `${text.slice(0, 137)}...` : text;
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  private resolveTotalRegistros(response: ConsultaList<Riesgos>, currentItems: number): number {
    const responseRecord = response as ConsultaList<Riesgos> & Record<string, unknown>;
    const candidates = [
      responseRecord?.pTotalItems,
      responseRecord?.totalItems,
      responseRecord?.totalRegistros,
      responseRecord?.param4,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
